import { AccessToken, Environment, MGMAuth } from '@mgmresorts/mgm-auth-js-sdk';
import { LogLevel } from '@mgmresorts/mgm-client-logging';
import { CircularProgress } from '@mgmresorts/mgm-ui';
import React, { createContext, useEffect, useMemo, useCallback, useRef } from 'react';

import { OktaUser } from '../../../@types/okta-user';
import { FlagsProvider } from '../../common/context/FlagsContext';
import { useAppRedirect } from '../../hooks/useAppRedirect';
import { useContent } from '../../hooks/useContent';
import { useIdxApiEnabled } from '../../hooks/useIdxApiEnabled';
import { usePreventAutoSignIn } from '../../hooks/usePreventAutoSignIn';
import { ClientId } from '../constants';
import { getFeaturesByClientId } from '../getFeaturesByClientId';
import { getThirdPartyScopesByClientId } from '../getScopes';
import { linkAccountToPartner } from '../linkAccountToPartner';
import { actually } from '../logging';
import { getPersistedParameters, ignoreParams } from '../persistParameterContext';

type Context = {
  authClient: MGMAuth;
  getUserInfo: () => Promise<OktaUser | null>;
  acquireToken: () => Promise<AccessToken | undefined>;
  acquireTransientToken: () => Promise<AccessToken | undefined>;
};

export const AuthContext = createContext<Context>({} as Context);

const NON_AUTO_REDIRECT_URLS: string[] = [
  '/identity/auth/login',
  '/identity/auth/mobile',
  '/identity/change-password',
  '/identity/external/authorize/marriott',
  '/identity/external/authentication/east',
  /** MFA Enrollment */
  '/identity/mfa/enroll',
  '/identity/mfa/enroll/email',
  '/identity/mfa/enroll/sms',
  '/identity/mfa/enroll/sms-verify',
  '/identity/mfa/enroll/passkey',
  /** MFA Authentication */
  '/identity/mfa/auth/email',
  '/identity/mfa/auth/sms',
  '/identity/mfa/auth/passkey',
];

export const AuthContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const redirectToApp = useAppRedirect();
  const params = getPersistedParameters();
  const { getContentByPath } = useContent();

  const { client_id = 'mgm_app_web' }: { client_id?: string } = params;
  const { isAllowOktaSignInWithRedirect, isEligibleToPreventAutoSignIn } = getFeaturesByClientId(client_id as ClientId);
  const { isAutoSignInPrevented, areFlagsLoaded } = usePreventAutoSignIn(client_id as ClientId);
  const enableIdxApi = useIdxApiEnabled();

  const localOverrides = useMemo(() => {
    return window.location.hostname === 'localhost'
      ? {
          identityAuthOrigin: `http://${window.location.host}/identity`,
          debugMode: true,
          logLevel: LogLevel.trace,
        }
      : {};
  }, []);

  const logLevel = process.env.REACT_APP_LOG_LEVEL
    ? (Number(process.env.REACT_APP_LOG_LEVEL) as LogLevel)
    : process.env.REACT_APP_ENVIRONMENT === 'prod'
    ? LogLevel.warn
    : LogLevel.log;

  const authConfig = useMemo(
    () => ({
      clientId: client_id as string,
      environment: (process.env.REACT_APP_ENVIRONMENT?.toUpperCase() ?? 'DEV') as Environment,
      appName: 'Identity Widget',
      logLevel: logLevel,
      enableIdxApi,
      allowOktaSignInWithRedirect: isAllowOktaSignInWithRedirect,
      ...(isAllowOktaSignInWithRedirect && { scopes: getThirdPartyScopesByClientId(client_id as ClientId) }),
      ...localOverrides,
    }),
    [client_id, isAllowOktaSignInWithRedirect, localOverrides, logLevel, enableIdxApi],
  );

  const authClient = useMemo(() => new MGMAuth(authConfig), [authConfig]);
  window.mgm = { ...window.mgm, authClient };

  const getUserInfo = useCallback(async () => {
    const activeSessionObj = await authClient.hasActiveMemberSession();
    if (activeSessionObj?.activeMemberSession && activeSessionObj?.session?.user) {
      const user = (await activeSessionObj.session.user()) as OktaUser;
      return user;
    }
    return null;
  }, [authClient]);

  const acquireToken = useCallback(async () => {
    const token = await authClient.acquireUserTokens();
    return token;
  }, [authClient]);

  const transientTokenPromiseRef = useRef<Promise<AccessToken | undefined> | null>(null);

  const acquireTransientToken = useCallback(async () => {
    if (transientTokenPromiseRef.current) return transientTokenPromiseRef.current;

    transientTokenPromiseRef.current = authClient.acquireUserTokens(true).finally(() => {
      transientTokenPromiseRef.current = null;
    });

    return transientTokenPromiseRef.current;
  }, [authClient]);

  const verifyPartnerExpiry = useCallback(() => {
    if (params.partner && params.tokenKey && !params.message?.includes(`${params.partner}_error`)) {
      const partnerExpiry = getContentByPath<string>(`signIn.partnerExpiry.${params.partner}`);

      if (partnerExpiry && parseInt(`${new Date().valueOf()}`) > parseInt(`${new Date(partnerExpiry).valueOf()}`)) {
        let search = '';
        for (const param in params) {
          if (!ignoreParams.includes(param)) {
            search += `&${param}=${params[param]}`;
          }
        }

        try {
          window.location.href = new URL(
            `${window.location.protocol}//${window.location.host}/identity?message=${params.partner}_error_expired${search}`,
          ).toString();

          return true;
        } catch (e: unknown) {
          actually.warn('could not parse partner expiry url');
        }
      }
    }

    return false;
  }, [getContentByPath, params]);

  const redirectHandledRef = useRef(false);

  // If the user is already logged in, bounce them back out.
  useEffect(() => {
    if (redirectHandledRef.current) return;

    // For clients eligible for close-active-session, wait until feature flags are loaded
    if (isEligibleToPreventAutoSignIn && !areFlagsLoaded) return;

    redirectHandledRef.current = true;

    async function redirectIfLoggedIn() {
      try {
        if (isAutoSignInPrevented) {
          const { activeMemberSession } = await authClient.hasActiveMemberSession();
          activeMemberSession && (await authClient.signOut());
          return;
        }

        const token = await authClient.acquireUserTokens(true);

        if (token && !token.isTransient) {
          /**
           * Verify the Okta session is alive before acting on the cached token.
           * Token Manager is cleared locally on session absence to avoid auto-redirects.
           * We do NOT call endSession() here, as it revokes tokens and closes the Okta
           * session remotely, which would poison any subsequent sign-in flow.
           */
          const { activeMemberSession } = await authClient.hasActiveMemberSession();

          if (!activeMemberSession) {
            authClient.getAuthClient().tokenManager.clear();

            return;
          }

          if (params.partner && params.tokenKey && token.mlife) {
            await linkAccountToPartner(params.partner as string, params.tokenKey as string, token.mlife);
          }

          if (isAllowOktaSignInWithRedirect) {
            // Use handleOktaSignInWithRedirect public SDK method to perform a standard /authorize redirect.
            await authClient.handleOktaSignInWithRedirect();
            return;
          }

          redirectToApp();
        }
      } catch (error) {
        console.error('[Auth Redirect] An error occurred while attempting to redirect you.');
      }
    }

    if (verifyPartnerExpiry()) return;

    if (
      !NON_AUTO_REDIRECT_URLS.includes(window.location.pathname) &&
      !params.message?.includes(`${params.partner}_error`)
    ) {
      redirectIfLoggedIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoSignInPrevented, areFlagsLoaded]);

  const contextValue = useMemo(
    () => ({ authClient, getUserInfo, acquireToken, acquireTransientToken }),
    [authClient, getUserInfo, acquireToken, acquireTransientToken],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      <FlagsProvider>
        <div style={{ position: 'relative' }}>
          {isEligibleToPreventAutoSignIn && !areFlagsLoaded && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 1000,
              }}
            >
              <CircularProgress />
            </div>
          )}
          {children}
        </div>
      </FlagsProvider>
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context: Context = React.useContext<Context>(AuthContext);

  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthContextProvider');
  }

  return context;
};
